import { XMarkIcon } from "@heroicons/react/24/outline";
import { useId } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import {
  ConfidenceLabel,
  ProposalPricebookMatchButton,
  PurchaseOrderMatchButton,
} from "@/accounts-payable/common";
import type { ApAgentAction, ApDocumentType } from "@/accounts-payable/types";
import {
  actionTitle,
  approveLabel,
  documentTypeLabel,
  isNoOpAction,
  proposalLinkAriaLabel,
  purchaseOrderProposalLink,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

export const ActionRow = ({
  action,
  runActions,
  businessId,
  document,
  showDivider,
  onResolve,
}: {
  action: ApAgentAction;
  /** Every action of the action's run, to label a PO that creates new items. */
  runActions: ApAgentAction[];
  businessId: string | null;
  /**
   * The document the action is for. With it, a PO or pricebook proposal
   * offers choosing the PO or the line's item by hand instead, as the review
   * page does.
   */
  document?: {
    id: string;
    type: ApDocumentType;
    /** The vendor's document number, naming the row's links for screen readers. */
    number: string | null;
    /** The document's newest run id, to tell whether a pick queued a new one. */
    agentRunId: string | null;
    hasJob: boolean;
  };
  /** False for the first row — the band above it already draws that edge. */
  showDivider: boolean;
  onResolve: (decision: "approved" | "rejected") => void;
}) => {
  const noOp = isNoOpAction(action);
  // The API's reason it would refuse the approval (e.g. a PO whose new
  // pricebook item was dismissed); the review card shows the same text.
  const blockedReason = action.approval_blocked_reason;
  const blockedReasonId = useId();
  const poLink = document
    ? purchaseOrderProposalLink(action, document.hasJob)
    : null;
  const kind = document ? documentTypeLabel(document.type).lower : "";
  // The way out when the proposal's target is wrong rather than its details:
  // the pick supersedes the proposal and has the agent re-check. A link on
  // the details line, so the Approve / ✕ column keeps its two edges.
  const fix =
    document && poLink ? (
      <PurchaseOrderMatchButton
        businessId={businessId}
        documentId={document.id}
        documentType={document.type}
        agentRunId={document.agentRunId}
        hasJob={poLink.hasJob}
        label={poLink.label}
        ariaLabel={proposalLinkAriaLabel(
          poLink.label,
          `choose the purchase order for ${kind} ${document.number ?? ""}`.trim(),
        )}
        appearance="link"
        className="whitespace-nowrap text-sm"
      />
    ) : document ? (
      <ProposalPricebookMatchButton
        businessId={businessId}
        documentId={document.id}
        documentType={document.type}
        documentNumber={document.number}
        agentRunId={document.agentRunId}
        action={action}
        appearance="link"
        className="whitespace-nowrap text-sm"
      />
    ) : null;
  return (
    // The outer wrapper owns the horizontal padding so the hover tint runs to
    // the card's edges; the divider lives on the inner box so it stays inset.
    <Stack
      gap="none"
      fullWidth
      className="px-6 transition-colors hover:bg-bg-secondary/60"
    >
      <Stack
        direction="row"
        gap="md"
        align="start"
        justify="between"
        fullWidth
        className={cn("py-3", {
          "border-t border-stroke-tertiary": showDivider,
        })}
      >
        <Stack gap="none" className="min-w-0 flex-1">
          <span className="text-md">{actionTitle(action)}</span>
          {/* Traffic-light on the confidence figure so a scan down the column
            separates settled rows from the ones that repay a read: green ≥90%
            (bulk-approvable), amber 70–90%, red <70%. All semibold — at this
            size colour alone lets the green and amber recede. The % is
            always spelled out, so colour is never the only signal. */}
          {noOp ? (
            <Typography
              variant="captionW1"
              textColor="secondary"
              className="mt-0.5"
            >
              {action.rationale}
            </Typography>
          ) : (
            // No separator: on a narrow row the link wraps whole onto its
            // own line, where a leading or trailing dot would dangle.
            <Stack
              direction="row"
              gap="none"
              align="baseline"
              className="mt-0.5 flex-wrap gap-x-3"
            >
              <ConfidenceLabel value={action.confidence} />
              {fix}
            </Stack>
          )}
          {blockedReason && (
            <Typography
              id={blockedReasonId}
              variant="captionW1"
              textColor="negative"
              className="mt-0.5"
            >
              {blockedReason}
            </Typography>
          )}
        </Stack>
        {/* Dismiss as an icon keeps both row shapes nearly the same width, so
            flushing them right lines the pills up without a dead gap. The
            cluster overhangs the content box by 8px (-mr-2) so the ✕ glyph,
            inset in its 32px hit target, lands on the divider's right end. */}
        <Stack
          direction="row"
          gap="sm"
          align="center"
          justify="end"
          className="-mr-2 flex-none"
        >
          {/* Both shapes get both controls. On a flag the two are not the same
              act: approving records it acknowledged, rejecting records it
              dismissed, and ActionStatusGlyph renders those differently. One
              min-width keeps every pill on the same two edges. */}
          <Button
            size="xsPill"
            variant={noOp ? "tertiary" : "secondary"}
            className="min-w-[7.5rem]"
            onClick={() => onResolve("approved")}
            disabled={Boolean(blockedReason)}
            aria-describedby={blockedReason ? blockedReasonId : undefined}
          >
            {noOp ? "Mark as seen" : approveLabel(action, runActions)}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={noOp ? "Ignore" : "Dismiss"}
                onClick={() => onResolve("rejected")}
                className="flex h-8 w-8 flex-none items-center justify-center rounded-full text-tertiary-text transition-colors hover:bg-red-5 hover:text-red-70 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{noOp ? "Ignore" : "Dismiss"}</TooltipContent>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};
